import { useEffect, useState, useRef, useCallback, useContext } from "react";
import { BillingCycle, format, labels } from "components/shareable/UpgradeToProModal/utils";
import Sty, { Container, StyledLabelP, SymphonyTooltip } from "modules/Sty";
import { OrganizationStatus, PlaylistingCampaignInput, YouTubeCampaignData } from "modules/Types";
import getSymbolFromCurrency from "currency-symbol-map";
import {
    CheckoutType,
    PRO_MONTHLY_PLAN_PRICE,
    TEAM_MONTHLY_PLAN_PRICE,
    TEAM_PLAN_ADDITIONAL_PRO_ACCOUNT_PRICE,
    PRO_ANNUAL_BY_MONTH_PLAN_PRICE,
    TEAM_ANNUAL_BY_MONTH_PLAN_PRICE,
    ONE_OFF_CAMPAIGN_PRICE,
    DEFAULT_CURRENCY,
    AdPlatform,
    PlaybookType,
    PlanType,
    PlaybookStepName
} from "modules/Const";
import { getNumberWithDecimals } from "helpers/General";
import useBrand, { BrandExistsState } from 'Hooks/useBrand';
import useOrganization from 'Hooks/useOrganization';
import { clsx, formatNumberToDisplayType, showToast, styx } from 'modules/Utils';
import Colors from "modules/Colors";
import useUser from "Hooks/useUser";
import ButtonSymphony from "elements/ButtonSymphony";
import { getConvertedCurrency_api } from "api/campaignApi";
import PayPalButtonGroup from "pages/MarketingPage/Components/GoLiveCard/PaypalCheckoutOption";
import useCampaignBuilder from "Hooks/useCampaignBuilder";
import { toastError } from "modules/Toast";
import { useHistory } from "react-router-dom";
import { CSSProperties } from "react";
import { track } from "analytics";
import useFacebookConnections from "Hooks/useFacebookConnections";
import CardSymphony from "elements/CardSymphony";
import debounce from 'lodash/debounce';
import SwitchSymphony, { SwitchType } from "elements/SwitchSymphony";
import Banner from "elements/Banner";
import { UpgradeModalContext } from "Hooks/UpgradeModalContext";
import CampaignCheckoutView from '../../elements/CampaignCheckoutView';
import UnifiedPaymentForm from 'elements/UnifiedPaymentForm';
import { ConfirmPaymentResponse } from "api/paymentsV2Api";
import useScreen from "Hooks/useScreen";

type CurrencyPrices = {
    annualConverted: number,
    monthlyConverted: number,
    oneOffConverted: number
}

const SubmitOrCheckoutButtonsView = ({
    overrideCampaignInput,
    style
}: {
    /** Used for old campaigns not supported in new Campaign store */
    overrideCampaignInput?:
    YouTubeCampaignData | PlaylistingCampaignInput,
    style?: React.CSSProperties
}) => {
    const history = useHistory();
    const {
        organization: primaryOrganization,
        brandIdIsInOrganization,
    } = useOrganization()
    const {
        currentBrand,
        isProBrand,
        getBrands,
        getShouldShowNewUserUpgradePrompt // Using the new selector
    } = useBrand() as BrandExistsState

    // Check if user is on a plan that supports direct campaign charging
    const isOnDirectChargePlan = currentBrand && [
        PlanType.STARTER,
        PlanType.GROWTH,
        PlanType.TEAM_V2
    ].includes(currentBrand.tier as PlanType)
    const { proFreeTrialRedeemed } = useUser()
    const { mobileView } = useScreen();
    const {
        submitCampaign,
        campaignAllowsDirectPayment,
        campaignDisclaimer,
        isSubmittingCampaign,
        adPlatform,
        playbookType,
        isGeographyBucketsObjectValid,
        budget: campaignBuilderBudget,
        automationFeeData,
        isLoadingAutomationFee,
        getAutomationFee,
        campaignConfirmationChecked,
        setCampaignConfirmationChecked,
        getConfirmationDisclaimerText,
        isConfirmationRequired,
        usesExpressSetup,
        currentStep
    } = useCampaignBuilder()

    // New upgrade check based on the selector from useBrand
    const requiresUpgradeToCheckout = getShouldShowNewUserUpgradePrompt();

    const allowsDirectPayment = campaignAllowsDirectPayment()

    // Extract the most repetitive logic
    const isConfirmationBlocking = isConfirmationRequired() && !campaignConfirmationChecked

    const [currencyPrices, setCurrencyPrices] = useState<CurrencyPrices>({
        annualConverted: PRO_ANNUAL_BY_MONTH_PLAN_PRICE,
        monthlyConverted: PRO_MONTHLY_PLAN_PRICE,
        oneOffConverted: ONE_OFF_CAMPAIGN_PRICE
    })

    const [billingCycle, setBillingCycle] = useState<BillingCycle>(BillingCycle.MONTHLY)

    const currencyCode = currentBrand.currency?.code ?? DEFAULT_CURRENCY

    const freeTrialOver = proFreeTrialRedeemed()
    // get the currency codes
    const currencySymbol = getSymbolFromCurrency(currencyCode)

    const [monthlyAdditionalProPlanPrice, setMonthlyAdditionalProPlanPrice] = useState<number>(TEAM_PLAN_ADDITIONAL_PRO_ACCOUNT_PRICE)

    // for non-Pro users, we show the option to run a one-off campaign or upgrade to Pro
    const [selectedTab, setSelectedTab] = useState<CheckoutType>(CheckoutType.ONE_OFF_CAMPAIGN)

    const paypalOrderDetails = useRef<any>(); // Reference to store PayPal order details

    // Create debounced version of automation fee calculation
    const debouncedGetAutomationFee = useCallback(
        debounce(() => {
            getAutomationFee();
        }, 400), // Shorter debounce since this appears later in the flow
        [] // getAutomationFee is stable from Zustand store
    );

    // Cleanup debounced function on unmount
    useEffect(() => {
        return () => {
            debouncedGetAutomationFee.cancel();
        };
    }, []); // debouncedGetAutomationFee is stable, no need to depend on it


    // Use campaign builder budget (video view campaigns now set this directly)
    const budget = campaignBuilderBudget
    // this is used ONLY for international currencies - specifically when a user selects an ad account with an international currency, and
    // we need to convert the "Upgrade to Pro" price to the international currency
    const getPricesValues = async () => {
        let baseMonthly;
        let baseAnnual;
        let oneOffCampaign;

        if (primaryOrganization && primaryOrganization.setup_status === OrganizationStatus.READY) {
            baseMonthly = await getConvertedCurrency_api({ from: DEFAULT_CURRENCY, to: currencyCode, amount: TEAM_MONTHLY_PLAN_PRICE })
            baseAnnual = await getConvertedCurrency_api({ from: DEFAULT_CURRENCY, to: currencyCode, amount: TEAM_ANNUAL_BY_MONTH_PLAN_PRICE })
        } else {
            baseMonthly = await getConvertedCurrency_api({ from: DEFAULT_CURRENCY, to: currencyCode, amount: PRO_MONTHLY_PLAN_PRICE })
            baseAnnual = await getConvertedCurrency_api({ from: DEFAULT_CURRENCY, to: currencyCode, amount: PRO_ANNUAL_BY_MONTH_PLAN_PRICE })
        }

        oneOffCampaign = await getConvertedCurrency_api({ from: DEFAULT_CURRENCY, to: currencyCode, amount: ONE_OFF_CAMPAIGN_PRICE })

        setCurrencyPrices({
            annualConverted: Number(getNumberWithDecimals(baseAnnual, 2)),
            monthlyConverted: Number(getNumberWithDecimals(baseMonthly, 2)),
            oneOffConverted: Number(getNumberWithDecimals(oneOffCampaign, 2))
        })
    }

    // Effect to fetch automation fees when budget or platform changes
    useEffect(() => {
        // Calculate fees for:
        // - One-off campaigns (always)
        // - Pro brands on YouTube/TikTok (still have automation fees)
        // - Non-pro brands (always)
        const shouldCalculateFees = budget && adPlatform && (
            selectedTab === CheckoutType.ONE_OFF_CAMPAIGN ||
            !isProBrand || // This will effectively be !isPaidBrand after full refactor
            (isProBrand && (adPlatform === AdPlatform.YOUTUBE || adPlatform === AdPlatform.TIKTOK))
        );

        if (shouldCalculateFees) {
            debouncedGetAutomationFee();
        }
    }, [budget, adPlatform, selectedTab, isProBrand]); // debouncedGetAutomationFee is stable

    useEffect(() => {
        if (currencyCode !== DEFAULT_CURRENCY) {
            if (primaryOrganization && primaryOrganization.setup_status === OrganizationStatus.READY) {
                getConvertedCurrency_api({ from: DEFAULT_CURRENCY, to: currencyCode, amount: TEAM_PLAN_ADDITIONAL_PRO_ACCOUNT_PRICE })
                    .then((res) => setMonthlyAdditionalProPlanPrice(Number(getNumberWithDecimals(res, 2))))
            }
            getPricesValues()
        } else {
            // TODO: remove this else statement when we fix the way we switch from brand to brand, right now it keeps some values unchanged
            if (currencyPrices.monthlyConverted !== PRO_MONTHLY_PLAN_PRICE) {
                setCurrencyPrices({
                    annualConverted: PRO_ANNUAL_BY_MONTH_PLAN_PRICE,
                    monthlyConverted: PRO_MONTHLY_PLAN_PRICE,
                    oneOffConverted: ONE_OFF_CAMPAIGN_PRICE
                })
            }
            if (primaryOrganization && primaryOrganization.setup_status === OrganizationStatus.READY) {
                setCurrencyPrices({
                    annualConverted: TEAM_ANNUAL_BY_MONTH_PLAN_PRICE,
                    monthlyConverted: TEAM_MONTHLY_PLAN_PRICE,
                    oneOffConverted: ONE_OFF_CAMPAIGN_PRICE
                })
            }
        }
    }, [currencyCode])

    /** 
     * Creates a PayPal order through the campaign checkout API
     * This is used for both Pro users and one-off campaigns
     * @returns Promise resolving to the PayPal order ID
     */
    const createPaypalOrder = async () => {
        try {
            if (isSubmittingCampaign) return '';

            // Set loading state
            // isSubmittingCampaign is already managed by the useCampaign hook

            // Determine which checkout type to use based on the selected tab
            const checkoutType = selectedTab === CheckoutType.ONE_OFF_CAMPAIGN ?
                'oneOffCheckout' : 'upgradePlanCheckout';

            // Make API call to create checkout session
            // We use the same submitCampaign function for both card and PayPal
            const response = await submitCampaign({
                submitType: checkoutType,
                recurring_interval: billingCycle,
                overrideCampaignInput
            });

            if (!response) {
                throw new Error('Failed to create PayPal order');
            }

            // Store order details for use in onApprove callback
            paypalOrderDetails.current = {
                orderId: response?.paypalOrderId || 'mock-order-id', // PayPal order ID from API
                campaignId: response?.campaign?.id || response?.id   // Campaign ID to redirect to later
            };

            return paypalOrderDetails.current.orderId;
        } catch (error) {
            console.error('Error creating PayPal order:', error);
            toastError('There was an error creating your PayPal order. Please try again.');
            return '';
        }
    }

    /**
     * Handles cancellation of PayPal checkout flow
     */
    const onCancelPayPalOrder = () => {
        // Reset loading state if needed
        // Track cancellation for analytics if necessary
        console.log('PayPal checkout cancelled');
        return Promise.resolve();
    }

    /**
     * Handles errors in the PayPal checkout flow
     * @param error The error object from PayPal
     */
    const onErrorPayPalOrder = () => {
        console.error('PayPal error occurred');
        toastError('There was an error processing your PayPal payment. Please try again.');
        return Promise.resolve();
    }

    /**
     * Handles successful PayPal order approval
     * Redirects to the campaign submission success page
     */
    const onPayPalOrderApprove = async () => {
        try {
            if (!paypalOrderDetails.current) {
                throw new Error('PayPal order details not found');
            }

            const { campaignId } = paypalOrderDetails.current;

            if (campaignId) {
                // Navigate to the campaign submission success page
                history.push(`/marketing2/submission/${campaignId}`);
            } else {
                throw new Error('Campaign ID not found in PayPal order details');
            }

            return Promise.resolve();
        } catch (error) {
            console.error('Error processing PayPal approval:', error);
            toastError('There was an error processing your payment. Please contact support.');
            return Promise.resolve();
        }
    }

    /**
     * Renders the confirmation checkbox for campaigns that require user confirmation
     * @param wrapInBanner Whether to wrap the switch in a purple Banner component
     * @param additionalStyle Additional styles to apply to the switch
     */
    const renderConfirmation = () => {
        if (!isConfirmationRequired()) {
            return null;
        }

        const switchComponent = (
            <SwitchSymphony
                className="campaignConfirmationCheckbox"
                type={SwitchType.SWITCH}
                checked={campaignConfirmationChecked}
                onChange={(checked) => setCampaignConfirmationChecked(checked)}
                label={getConfirmationDisclaimerText()}
            />
        );

        return (
            <Banner color="purple" className="confirmationBanner">
                {switchComponent}
            </Banner>
        );
    }

    /**
     * Renders the PayPal button component
     * @param allowNonPro Whether to allow non-Pro users to use PayPal (for one-off campaigns)
     */
    const renderPaypalButton = (allowNonPro: boolean = false) => {
        return null
        return (
            <PayPalButtonGroup
                createOrder={createPaypalOrder}
                onCancel={onCancelPayPalOrder}
                onError={onErrorPayPalOrder}
                onApprove={onPayPalOrderApprove}
                className="w-full"
                currencyCode={currencyCode}
                style={{ marginTop: 8 }}
                allowWithoutPro={allowNonPro}
            />
        )
    }

    const handleClickSubmitButton = async (submitType: 'directCreate' | 'upgradePlanCheckout' | 'oneOffCheckout') => {
        try {
            let res = await submitCampaign({ submitType, recurring_interval: billingCycle, overrideCampaignInput });

            // If we've redirected to Stripe already (happens in upgradePlanCheckout case), don't do anything else
            if (res?.redirected) {
                // Already redirected to Stripe, nothing more to do
                return;
            }

            track("Submit Campaign", {
                adPlatform,
                playbookType,
                brand_id: currentBrand.id,
                brand_slug: currentBrand.slug,
                brand_name: currentBrand.name,
            })

            // For direct creation or when the user is already on a paid plan (no URL redirect needed)
            if (res?.campaign?.id) {
                // Navigate to the campaign submission success page
                history.push(`/marketing2/submission/${res.campaign.id}`);
            } else if (submitType === 'directCreate' && res?.id) {
                // Handle case where response directly contains the campaign ID
                history.push(`/marketing2/submission/${res.id}`);
            }
        } catch (error) {
            console.error('error: ', error);
            toastError('There was an error submitting your campaign. Please try again.');
        }
    }

    /**
     * Renders the view for Pro users, showing submission UI
     */
    const renderProView = () => {

        let mainComponent = (
            <>
                {/* Confirmation checkbox for TikTok/YouTube campaigns */}
                {renderConfirmation()}

                <div className="campaignDisclaimer" style={styx(Sty.text16grey, { textAlign: 'center', marginBottom: 16 })} >
                    {campaignDisclaimer}
                </div>
                <ButtonSymphony
                    className="submitCampaignButton1"
                    onClick={() => handleClickSubmitButton('directCreate')}
                    width="100%"
                    disabled={isSubmittingCampaign || !isGeographyBucketsObjectValid || isConfirmationBlocking}
                    isLoading={isSubmittingCampaign}
                >
                    Submit Campaign
                </ButtonSymphony>
            </>
        )

        return (
            <div className={clsx(`submitCampaignOrUpgradeComponent`)} style={style}>
                {mainComponent}
            </div>
        );
    }

    /**
     * Renders the view for direct campaign charging (starter/growth/team_v2 plans)
     */
    const renderDirectChargeView = () => {

        let transformedCampaignInput: any = overrideCampaignInput || useCampaignBuilder().getDataForSubmission()
        transformedCampaignInput.budget = Number(campaignBuilderBudget)
        const paymentData = {
            type: 'campaign' as const,
            planType: currentBrand?.tier as PlanType,
            campaignInput: transformedCampaignInput,
            usesExpressSetup: transformedCampaignInput.usesExpressSetup,
        }

        const handlePaymentSuccess = (result: any) => {
            track("Submit Campaign", {
                adPlatform,
                playbookType,
                brand_id: currentBrand.id,
                brand_slug: currentBrand.slug,
                brand_name: currentBrand.name,
            })

            if (result.campaignId) {
                history.push(`/marketing2/submission/${result.campaignId}`);
            }
        }

        const handlePaymentError = (error: string) => {
            console.error('Campaign payment error:', error);
            toastError('There was an error processing your payment. Please try again.');
        }

        // Use campaign builder budget (video view campaigns now set this directly)
        const budget = campaignBuilderBudget

        // Always use server-provided automation fee data for line items
        let marketingBudget = 0;
        let automationFee = 0;
        let totalAmount = 0;
        let feeDescription = 'Platform fee for campaign management and optimization';
        let feeTitle = "Automation Fee";
        let automationFeeSubtitle = 'Platform Fee';

        if (automationFeeData) {
            // Use backend-calculated values
            marketingBudget = automationFeeData.adsBudget;
            automationFee = automationFeeData.takeRate;
            totalAmount = automationFeeData.totalCost;

            // Get fee details from lineItems
            const automationFeeItem = automationFeeData.lineItems.find(item => item.type === 'automation_fee');
            if (automationFeeItem) {
                feeDescription = automationFeeItem.description;
                feeTitle = automationFeeItem.name;
                automationFeeSubtitle = automationFeeItem.name;
            }
        }

        return (
            <div className={clsx(`submitCampaignOrUpgradeComponent directChargeView`)} style={style}>
                <div style={{ marginBottom: 16 }}>
                    <CardSymphony
                        className="directChargeCampaignCard"
                        isNoPadding
                        title="Checkout + Submit"
                        subtitle="Your campaign will be charged directly and submitted for processing."
                    />
                </div>

                <div>
                    {isLoadingAutomationFee ? (
                        /* Loading State */
                        <div style={{
                            padding: 24,
                            textAlign: 'center',
                            color: '#707070',
                            backgroundColor: Colors.greyLight,
                            borderRadius: 8
                        }}>
                            <div style={{ fontSize: 16, marginBottom: 8 }}>Calculating fees...</div>
                            <div style={{ fontSize: 14 }}>Please wait while we determine the best pricing for your campaign</div>
                        </div>
                    ) : (
                        <>
                            {/* Marketing Budget */}
                            {usesExpressSetup() && marketingBudget > 0 && (
                                <div
                                    className="marketingBudgetCard"
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        padding: '16px 0',
                                        borderBottom: '1px solid #f0f0f0'
                                    }}
                                >
                                    <div style={Sty.text16bold}>Ads Budget</div>
                                    <div style={Sty.text16}>{formatNumberToDisplayType(marketingBudget, 'currency')}</div>
                                </div>
                            )}

                            {/* Automation Fee */}
                            {automationFee > 0 && (
                                <div
                                    className="automationFeeCard"
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        padding: '16px 0',
                                        borderBottom: '1px solid #f0f0f0'
                                    }}
                                >
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ fontWeight: 500 }}>
                                            {automationFeeSubtitle}
                                        </div>
                                        <div style={{ fontSize: 12, color: '#707070' }}>{feeDescription}</div>
                                    </div>
                                    <div>{formatNumberToDisplayType(automationFee, 'currency')}</div>
                                </div>
                            )}

                            {/* Total */}
                            <div
                                className="totalCard"
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    padding: 16,
                                    backgroundColor: Colors.greyLight,
                                    borderRadius: 8,
                                    marginTop: 16,
                                    marginBottom: 16
                                }}
                            >
                                <div style={Sty.text16bold}>Total charged today</div>
                                <div style={Sty.text16}>{formatNumberToDisplayType(totalAmount, 'currency')}</div>
                            </div>
                        </>
                    )}
                </div>

                {/* Confirmation checkbox for TikTok/YouTube campaigns */}
                {renderConfirmation()}

                <div className="campaignDisclaimer" style={styx(Sty.text16grey, { textAlign: 'center', marginBottom: 16 })} >
                    {campaignDisclaimer}
                </div>

                <UnifiedPaymentForm
                    shouldLoadIntent={currentStep === PlaybookStepName.FINALIZATION}
                    paymentData={paymentData}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    submitButtonText={isLoadingAutomationFee ? 'Calculating fees...' : 'Checkout + Submit'}
                />
            </div>
        );
    }

    const renderOneOffCampaignView = (isPro: boolean = false) => {
        // Always use server-provided automation fee data
        let marketingBudget = 0;
        let automationFee = 0;
        let totalAmount = 0;
        let feeDescription = 'Platform fee for campaign management and optimization';
        let feeTitle = "Automation Fee";
        let automationFeeSubtitle = 'Platform Fee';

        if (automationFeeData) {
            // Use backend-calculated values
            marketingBudget = automationFeeData.adsBudget;
            automationFee = automationFeeData.takeRate;
            totalAmount = automationFeeData.totalCost;

            // Get fee details from lineItems
            const automationFeeItem = automationFeeData.lineItems.find(item => item.type === 'automation_fee');
            if (automationFeeItem) {
                feeDescription = automationFeeItem.description;
                feeTitle = automationFeeItem.name;
                automationFeeSubtitle = automationFeeItem.name;
            }
        }
        return (
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 24,
                    ...style
                }}
            >
                {/* Header */}
                <div style={{ marginBottom: 16 }}>
                    <CardSymphony
                        className="oneOffCampaignCard"
                        isNoPadding
                        title="Let's go live"
                        subtitle="Your campaign is ready to go live. Click the button below to submit your campaign and start running ads."
                    />
                </div>
                <div>
                    {isLoadingAutomationFee ? (
                        /* Loading State */
                        <div style={{
                            padding: 24,
                            textAlign: 'center',
                            color: '#707070',
                            backgroundColor: Colors.greyLight,
                            borderRadius: 8
                        }}>
                            <div style={{ fontSize: 16, marginBottom: 8 }}>Calculating fees...</div>
                            <div style={{ fontSize: 14 }}>Please wait while we determine the best pricing for your campaign</div>
                        </div>
                    ) : (
                        <>
                            {/* Marketing Budget */}
                            {marketingBudget > 0 && (
                                <div
                                    className="marketingBudgetCard"
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        padding: '16px 0',
                                        borderBottom: '1px solid #f0f0f0'
                                    }}
                                >
                                    <div style={Sty.text16bold}>Ads Budget</div>
                                    <div style={Sty.text16}>{formatNumberToDisplayType(marketingBudget, 'currency')}</div>
                                </div>
                            )}

                            {/* Automation Fee */}
                            {automationFee > 0 && (
                                <div
                                    className="oneOffCampaignCard"
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        padding: '16px 0',
                                        borderBottom: '1px solid #f0f0f0'
                                    }}
                                >
                                    <div style={{ display: 'flex', flexDirection: 'column' }}>
                                        <div style={{ fontWeight: 500 }}>
                                            {automationFeeSubtitle}
                                        </div>
                                        <div style={{ fontSize: 12, color: '#707070' }}>{feeDescription}</div>
                                    </div>
                                    <div>{formatNumberToDisplayType(automationFee, 'currency')}</div>
                                </div>
                            )}

                            {/* Total */}
                            <div
                                className="totalCard"
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    padding: 16,
                                    backgroundColor: Colors.greyLight,
                                    borderRadius: 8,
                                    marginTop: 16
                                }}
                            >
                                <div style={Sty.text16bold}>Total charged today</div>
                                <div style={Sty.text16}>{formatNumberToDisplayType(totalAmount, 'currency')}</div>
                            </div>
                        </>
                    )}
                </div>

                {/* Confirmation checkbox for TikTok/YouTube campaigns */}
                {renderConfirmation()}

                <StyledLabelP color="#707070" fontSize={16} fontWeight={400}>
                    {campaignDisclaimer}
                </StyledLabelP>

                <ButtonSymphony
                    className="checkoutWithCardButton"
                    width="100%"
                    isLoading={isSubmittingCampaign || isLoadingAutomationFee}
                    disabled={isSubmittingCampaign || !isGeographyBucketsObjectValid || isLoadingAutomationFee || (isConfirmationRequired() && !campaignConfirmationChecked)}
                    onClick={() => handleClickSubmitButton('oneOffCheckout')}
                >
                    {isLoadingAutomationFee ? 'Calculating fees...' : 'Checkout with Card'}
                </ButtonSymphony>

                {/* Allow PayPal for one-off campaigns even for non-Pro users */}
                {currencyCode === 'USD' && renderPaypalButton(true)}
            </div>
        )
    }

    // REMOVED renderUpgradePlanView() function as per spec (lines 678-788)
    const onPaidCampaignCheckout = async (result: ConfirmPaymentResponse) => {
        console.log('result', result)
        const campaignId = result.campaignId
        await getBrands(true)
        console.log('campaignId', campaignId)
        if (!campaignId) {
            showToast({
                message: "Payment succeeded but campaign creation failed. Please contact support.",
                error: true,
                mobile: mobileView
            });
        } else {
            history.push(`/marketing2/submission/${campaignId}`);
        }
    }
    // Check if user needs to upgrade before checkout
    if (requiresUpgradeToCheckout) {
        return (
            <div
                id="campaignSubmitButtonsContainer"
                className="campaignSubmitButtonsContainer submitOrCheckoutButtonsView"
                data-testid="campaign-submit-buttons"
            >
                <CampaignCheckoutView
                    className="campaignPlanSelection"
                    style={style}
                    onPaid={onPaidCampaignCheckout}
                    isCampaignBundle={true}
                    overrideCampaignInput={overrideCampaignInput}
                />
            </div>
        );
    }

    let viewToRender;

    // For users on direct charge plans, always use the direct charge view
    if (isOnDirectChargePlan) {
        viewToRender = renderDirectChargeView();
    } else {
        switch (adPlatform) {
            case AdPlatform.PLAYLISTS:
                viewToRender = (
                    <>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 8,
                            marginTop: 16
                        }}>
                            {campaignDisclaimer && <div className="campaignDisclaimer" style={styx(Sty.text16grey, { textAlign: 'center', marginBottom: 16 })} >
                                {campaignDisclaimer}
                            </div>}
                            <ButtonSymphony
                                className="submitCampaignOrUpgradeButton"
                                width="100%"
                                onClick={() => handleClickSubmitButton('oneOffCheckout')}
                                disabled={isSubmittingCampaign || isConfirmationBlocking}
                                isLoading={isSubmittingCampaign}
                            >
                                Checkout with Card
                            </ButtonSymphony>
                            {/* TODO MEGH: Re-add paypal button */}
                            {/* Pro users can always use PayPal if they have USD currency */}
                            {renderPaypalButton(false)}
                        </div>
                    </>
                );
                break;
            case AdPlatform.TIKTOK:
            case AdPlatform.YOUTUBE:
                viewToRender = renderOneOffCampaignView(isProBrand);
                break;
            case AdPlatform.META:
                if (allowsDirectPayment || !isProBrand) {
                    viewToRender = renderOneOffCampaignView(isProBrand);
                } else {
                    viewToRender = renderProView();
                }
                break;
        }
    }

    return (
        <div
            id="campaignSubmitButtonsContainer"
            className="campaignSubmitButtonsContainer submitOrCheckoutButtonsView"
            data-testid="campaign-submit-buttons"
        >
            {viewToRender}
        </div>
    );
}

export default SubmitOrCheckoutButtonsView